import { createReducer, createAction } from '@reduxjs/toolkit';
import qs from 'qs';
import normalize from './normalize';
import merge from 'lodash/merge';
import get from 'lodash/get';
import {
  UrlParams,
  ResourceIdentifier,
  JsonApiV2State,
  Resource,
  ReduxState,
  ActionSuccess,
} from './jsonApiTypes';
import ReactOnRails from 'react-on-rails';
import axios, { AxiosRequestConfig } from 'axios';
import handleJsonApiErrorDuck from '../../lib/functions/handleJsonApiErrorDuck';
import { toast } from 'react-toastify';

const reduceCount = (count: number) => (count - 1 < 0 ? 0 : count - 1);

const transformUrlParams = (params: UrlParams = {}) => {
  const transformedParams: {
    [key: string]: any;
  } = {};
  Object.keys(params).forEach(
    (key: 'fields' | 'include' | 'page' | 'sort' | 'filter') => {
      if (['fields', 'page', 'filter'].includes(key)) {
        const object = params[key] as {
          [key: string]: any;
        };
        Object.keys(object).forEach(innerKey => {
          transformedParams[`${key}[${innerKey}]`] = object[innerKey];
        });
      } else {
        transformedParams[key] = params[key];
      }
    }
  );
  return qs.stringify(params, {
    encode: true,
    arrayFormat: 'comma',
  });
};

const request = async (config: AxiosRequestConfig, options = {}) => {
  const notGetRequest =
    config.method && ['patch', 'delete', 'post'].includes(config.method);
  Object.assign(config, {
    headers: {
      'Content-Type': notGetRequest ? 'application/vnd.api+json' : undefined,
      ...ReactOnRails.authenticityHeaders(),
    },
    paramsSerializer: (params: UrlParams) => transformUrlParams(params),
    transformRequest: [
      (data: any) => {
        return data instanceof FormData ? data : { data };
      },
      ...(axios.defaults.transformRequest as []),
    ],
    transformResponse: (data: string) => {
      try {
        return normalize(JSON.parse(data), { includeOriginalResponse: true });
      } catch (error) {
        return undefined;
      }
    },
  });
  try {
    const response = await axios.request(config);
    return response.data;
  } catch (error) {
    if (error.response) {
      handleJsonApiErrorDuck(error.response, options);
    } else if (error.request) {
      toast.error(
        "We're having trouble connecting. Check your internet connection."
      );
    } else {
      toast.error('Something went wrong. Please refresh and try again.');
    }
    throw error;
  }
};

// ACTION CREATORS...
interface CreateResourceData {
  id?: string | number;
  type: string;
  attributes: {
    [key: string]: any;
  };
}

export interface CreateResourceOptions {
  urlParams?: UrlParams;
  onFulfilled?: (state: JsonApiV2State, result: object) => void;
  toast401?: boolean;
  namespace?: string;
}

export const createResourceV2 = createAction(
  'CREATE_RESOURCE',
  (
    endpoint: string,
    data: CreateResourceData | FormData,
    options: CreateResourceOptions = {}
  ) => ({
    payload: request(
      {
        url: endpoint,
        method: 'post',
        params: options.urlParams,
        data,
      },
      options
    ),
    meta: options,
  })
);

export const deleteResourceV2 = createAction(
  'DELETE_RESOURCE',
  (
    endpoint: string,
    type: string,
    id: string | number,
    removeResource = true
  ) => ({
    meta: { type, id, removeResource },
    payload: request({
      url: endpoint,
      method: 'delete',
    }),
  })
);

export const disableSpinnerV2 = createAction('DISABLE_SPINNER');

export const readEndpointV2 = createAction(
  'READ_ENDPOINT',
  (
    endpoint: string,
    namespace: string,
    options: { urlParams?: UrlParams } = {}
  ) => ({
    meta: { namespace },
    payload: request({
      url: endpoint,
      params: options.urlParams,
    }),
  })
);

// export const removeResultV2 = createAction(
//   'REMOVE_RESULT',
//   (namespace: string) => ({ payload: { namespace } })
// );

export const setIsBusyV2 = createAction('SET_IS_BUSY', (value?: number) => ({
  payload: { value },
}));

interface UpdateResourceData {
  id: string | number;
  type: string;
  attributes?: {
    [key: string]: any;
  };
  relationships?: {
    [key: string]: any;
  };
}

interface UpdateResourceOptions {
  namespace?: string;
  urlParams?: UrlParams;
}

export const updateResourceV2 = createAction(
  'UPDATE_RESOURCE',
  (
    endpoint: string,
    data: UpdateResourceData,
    options: UpdateResourceOptions = {}
  ) => ({
    payload: request({
      url: endpoint,
      params: options.urlParams,
      method: 'PATCH',
      data,
    }),
    meta: { namespace: options.namespace },
  })
);

export const initialState: JsonApiV2State = {
  isBusyForced: 0,
  isCreating: 0,
  isDeleting: 0,
  isReading: 0,
  isSpinnerDisabled: 0,
  isUpdating: 0,
  entities: {},
  results: {},
};

export default createReducer(initialState, {
  CREATE_RESOURCE_PENDING: state => {
    state.isCreating = state.isCreating + 1;
  },
  CREATE_RESOURCE_REJECTED: state => {
    state.isCreating = reduceCount(state.isCreating);
    state.isSpinnerDisabled = reduceCount(state.isSpinnerDisabled);
  },
  CREATE_RESOURCE_FULFILLED: (state, action) => {
    const { payload, meta } = action;
    const { result, originalResponse, ...resources } = payload;
    state.isCreating = reduceCount(state.isCreating);
    state.isSpinnerDisabled = reduceCount(state.isSpinnerDisabled);
    merge(state, {
      entities: resources,
    });
    if (meta.onFulfilled) meta.onFulfilled(state, result);
  },
  DELETE_RESOURCE_PENDING: state => {
    state.isDeleting = state.isDeleting + 1;
  },
  DELETE_RESOURCE_REJECTED: state => {
    state.isSpinnerDisabled = reduceCount(state.isSpinnerDisabled);
    state.isDeleting = reduceCount(state.isDeleting);
  },
  DELETE_RESOURCE_FULFILLED: (state, action) => {
    const { meta } = action;
    state.isDeleting = reduceCount(state.isDeleting);
    state.isSpinnerDisabled = reduceCount(state.isSpinnerDisabled);
    if (meta.removeResource) {
      delete state['entities'][meta.type][meta.id];
    }
  },
  [disableSpinnerV2.type]: state => {
    state.isSpinnerDisabled = state.isSpinnerDisabled + 1;
  },
  READ_ENDPOINT_PENDING: state => {
    state.isReading = state.isReading + 1;
  },
  READ_ENDPOINT_REJECTED: state => {
    state.isSpinnerDisabled = reduceCount(state.isSpinnerDisabled);
    state.isReading = reduceCount(state.isReading);
  },
  READ_ENDPOINT_FULFILLED: (state, action) => {
    const { meta, payload } = action;
    const { result, originalResponse, ...resources } = payload;
    merge(state, { entities: resources });
    state.results[meta.namespace] = { ...result };
    state.isReading = reduceCount(state.isReading);
    state.isSpinnerDisabled = reduceCount(state.isSpinnerDisabled);
  },
  REMOVE_RESULT: (state, action) => {
    delete state.results[action.payload.namespace];
  },
  SET_IS_BUSY: (state, action) => {
    const { value } = action.payload;
    state.isBusyForced = value || state.isBusyForced + 1;
  },
  UPDATE_RESOURCE_PENDING: state => {
    state.isUpdating = state.isUpdating + 1;
  },
  UPDATE_RESOURCE_REJECTED: state => {
    state.isUpdating = reduceCount(state.isUpdating);
    state.isSpinnerDisabled = reduceCount(state.isSpinnerDisabled);
  },
  UPDATE_RESOURCE_FULFILLED: (state, action: ActionSuccess) => {
    const { payload } = action;
    const { result, originalResponse, ...resources } = payload;

    for (const resourceType in resources) {
      for (const id in resources[resourceType]) {
        state['entities'][resourceType][id] = resources[resourceType][id];
      }
    }

    state.isSpinnerDisabled = reduceCount(state.isSpinnerDisabled);
    state.isUpdating = reduceCount(state.isUpdating);
  },
});

// SELECTORS
export const getMetaV2 = (namespace: string) => (state: ReduxState) =>
  get(state.jsonApiStoreV2, ['results', namespace, 'meta']);

export const getRelationshipV2 = (
  parentType: string,
  parentId: string | number,
  relationshipType: string
) => (state: ReduxState) => {
  const resourceIdentifierObject =
    get(state.jsonApiStoreV2, [
      'entities',
      parentType,
      parentId,
      'relationships',
      relationshipType,
      'data',
    ]) || {};
  return get(state.jsonApiStoreV2, [
    'entities',
    resourceIdentifierObject.type,
    resourceIdentifierObject.id,
  ]);
};

export const getRelationshipsV2 = (
  parentType: string,
  parentId: string | number,
  relationshipType: string
) => (state: ReduxState) => {
  const resourceIdentifiers = get(state.jsonApiStoreV2, [
    'entities',
    parentType,
    parentId,
    'relationships',
    relationshipType,
    'data',
  ]) as ResourceIdentifier[] | undefined;
  if (!resourceIdentifiers) return;
  return resourceIdentifiers
    .map(({ id, type }) => get(state.jsonApiStoreV2, ['entities', type, id]))
    .filter(resource => resource !== undefined);
};

export const getCurrentOrganizationV2 = (state: ReduxState) => {
  const { id, type } = state.jsonApiStoreV2.results['CURRENT_ORGANIZATION']
    .data as ResourceIdentifier;
  return state.jsonApiStoreV2.entities[type][id];
};

export const getCurrentUserV2 = (state: ReduxState) => {
  const { id, type } =
    (get(state.jsonApiStoreV2, ['results', 'CURRENT_USER', 'data']) as
      | ResourceIdentifier
      | undefined) || {};
  if (!id || !type) return;
  return get(state.jsonApiStoreV2, ['entities', type, id]) as
    | Resource
    | undefined;
};

export const getResourceV2 = (namespace: string) => (
  state: ReduxState
): Resource | undefined => {
  const { id, type } =
    (get(state.jsonApiStoreV2, ['results', namespace, 'data']) as
      | ResourceIdentifier
      | undefined) || {};
  if (!id || !type) return undefined;
  return get(state.jsonApiStoreV2, ['entities', type, id]);
};

export const getResourcesV2 = (namespace: string) => (state: ReduxState) => {
  const identifiers = get(state.jsonApiStoreV2, [
    'results',
    namespace,
    'data',
  ]) as ResourceIdentifier[] | undefined;
  return identifiers
    ? identifiers
        .map(({ id, type }) =>
          get(state.jsonApiStoreV2, ['entities', type, id])
        )
        .filter(resource => resource !== undefined)
    : undefined;
};

export const isBusyV2 = (state: ReduxState) =>
  state.jsonApiStoreV2.isCreating > 0 ||
  state.jsonApiStoreV2.isDeleting > 0 ||
  state.jsonApiStoreV2.isUpdating > 0 ||
  state.jsonApiStoreV2.isReading > 0 ||
  state.jsonApiStoreV2.isBusyForced > 0;

export const isSpinnerDisabledV2 = (state: ReduxState) =>
  state.jsonApiStoreV2.isSpinnerDisabled > 0;
